import {
  ChevronRight,
  LogIn,
  ServerCog,
  Settings2,
  Sparkles,
} from "lucide-react";
import type { ComposerActivity } from "./Composer";
import type { ToolPopupProps } from "./ComposerToolPopup";
import { t } from "../../i18n";
import { Toggle } from "../Toggle";
import { ModelSearchPicker } from "./ModelSearchPicker";
import { activityLabel } from "./ComposerActivityUtils";

type ComposerToolPopupToolsProps = Pick<
  ToolPopupProps,
  | "section"
  | "activityModels"
  | "activitySettings"
  | "enabledActivities"
  | "mcpServers"
  | "mcpServerIds"
  | "mcpToolSelections"
  | "mcpTools"
  | "onActivitySettingsChange"
  | "onOpenActivity"
  | "onOpenMcp"
  | "onOpenSettings"
  | "onSetupMcp"
  | "onToggle"
  | "onToggleActivity"
  | "onToggleMcp"
  | "toolIds"
  | "tools"
>;

export function ComposerToolPopupTools(props: ComposerToolPopupToolsProps) {
  const {
    section,
    activityModels,
    activitySettings,
    enabledActivities,
    mcpServers,
    mcpServerIds,
    mcpToolSelections,
    mcpTools,
    onActivitySettingsChange,
    onOpenActivity,
    onOpenMcp,
    onOpenSettings,
    onSetupMcp,
    onToggle,
    onToggleActivity,
    onToggleMcp,
    toolIds,
    tools,
  } = props;
  return (
    <>
      {section === "tools" && (
        <div className="flex flex-col">
          <div className="hidden">
            <div className="mb-1 flex items-start gap-2 px-2">
              <Sparkles size={15} className="mt-0.5 shrink-0 text-lumen-600" />
              <span>
                <strong className="block text-sm">{t("Activities")}</strong>
                <span className="block text-[11px] text-stone-500 dark:text-stone-400">
                  {t("Choose allowed activities and configure each one.")}
                </span>
              </span>
            </div>
            {(
              Object.keys(activityModels) as Array<
                Exclude<ComposerActivity, "ask">
              >
            ).map((item) => {
              const models = activityModels[item];
              const enabled = enabledActivities.includes(item);
              const settings = activitySettings[item] ?? {};
              const selectedModel = models.find(
                (model) => model.id === settings.modelId,
              );
              const metadata = selectedModel?.remoteMetadata;
              const rawVoices =
                metadata && typeof metadata === "object"
                  ? ((metadata as Record<string, unknown>).supported_voices ??
                    (metadata as Record<string, unknown>).supportedVoices)
                  : undefined;
              const voices = Array.isArray(rawVoices)
                ? rawVoices.flatMap((voice) => {
                    if (typeof voice === "string")
                      return [{ id: voice, label: voice }];
                    if (!voice || typeof voice !== "object") return [];
                    const value = voice as Record<string, unknown>;
                    const id =
                      typeof value.id === "string"
                        ? value.id
                        : typeof value.voice === "string"
                          ? value.voice
                          : null;
                    return id
                      ? [
                          {
                            id,
                            label:
                              typeof value.name === "string" ? value.name : id,
                          },
                        ]
                      : [];
                  })
                : [];
              return (
                <div
                  key={item}
                  className="rounded-lg p-2 hover:bg-stone-50 dark:hover:bg-white/5"
                >
                  <div className="flex items-center gap-2">
                    <Toggle
                      checked={enabled}
                      onChange={() => onToggleActivity(item)}
                    />
                    <span className="min-w-0 flex-1 text-sm font-semibold">
                      {activityLabel(item)}
                    </span>
                    {models.length === 0 && (
                      <span className="text-[10px] text-stone-400">
                        {t("Unavailable")}
                      </span>
                    )}
                  </div>
                  {enabled && models.length > 0 && (
                    <div className="mt-2 space-y-2 pl-7">
                      <ModelSearchPicker
                        models={models}
                        value={settings.modelId ?? ""}
                        emptyLabel={t("Let the agent choose")}
                        onChange={(modelId) =>
                          onActivitySettingsChange(item, {
                            ...settings,
                            modelId: modelId || undefined,
                          })
                        }
                      />
                      {(item === "audio" || item === "speech") &&
                        voices.length > 0 && (
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-medium text-stone-500">
                              {t("Voice")}
                            </span>
                            <select
                              className="field py-1.5 text-xs"
                              value={settings.voice ?? ""}
                              onChange={(event) =>
                                onActivitySettingsChange(item, {
                                  ...settings,
                                  voice: event.target.value || undefined,
                                })
                              }
                            >
                              <option value="">
                                {t("Let the agent choose")}
                              </option>
                              {voices.map((voice) => (
                                <option key={voice.id} value={voice.id}>
                                  {voice.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                      {(item === "image" || item === "video") && (
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-stone-500">
                              {t("Size")}
                            </span>
                            <select
                              className="field py-1.5 text-xs"
                              value={settings.size ?? ""}
                              onChange={(event) =>
                                onActivitySettingsChange(item, {
                                  ...settings,
                                  size: event.target.value || undefined,
                                })
                              }
                            >
                              <option value="">
                                {t("Let the agent choose")}
                              </option>
                              <option value="1024x1024">1024 × 1024</option>
                              <option value="1792x1024">1792 × 1024</option>
                              <option value="1024x1792">1024 × 1792</option>
                            </select>
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-stone-500">
                              {item === "image"
                                ? t("Quality")
                                : t("Aspect ratio")}
                            </span>
                            <select
                              className="field py-1.5 text-xs"
                              value={
                                (item === "image"
                                  ? settings.quality
                                  : settings.aspectRatio) ?? ""
                              }
                              onChange={(event) =>
                                onActivitySettingsChange(item, {
                                  ...settings,
                                  ...(item === "image"
                                    ? {
                                        quality:
                                          event.target.value || undefined,
                                      }
                                    : {
                                        aspectRatio:
                                          event.target.value || undefined,
                                      }),
                                })
                              }
                            >
                              <option value="">
                                {t("Let the agent choose")}
                              </option>
                              {item === "image" ? (
                                <>
                                  <option value="standard">
                                    {t("Standard")}
                                  </option>
                                  <option value="hd">HD</option>
                                </>
                              ) : (
                                <>
                                  <option value="16:9">16:9</option>
                                  <option value="9:16">9:16</option>
                                  <option value="1:1">1:1</option>
                                </>
                              )}
                            </select>
                          </label>
                        </div>
                      )}
                      {(item === "audio" ||
                        item === "speech" ||
                        item === "transcription") && (
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-stone-500">
                              {t("Format")}
                            </span>
                            <select
                              className="field py-1.5 text-xs"
                              value={settings.format ?? ""}
                              onChange={(event) =>
                                onActivitySettingsChange(item, {
                                  ...settings,
                                  format: event.target.value || undefined,
                                })
                              }
                            >
                              <option value="">
                                {t("Let the agent choose")}
                              </option>
                              {item === "transcription" ? (
                                <>
                                  <option value="text">{t("Text")}</option>
                                  <option value="json">{t("JSON")}</option>
                                  <option value="srt">{t("SRT")}</option>
                                </>
                              ) : (
                                <>
                                  <option value="mp3">{t("MP3")}</option>
                                  <option value="wav">{t("WAV")}</option>
                                  <option value="opus">{t("Opus")}</option>
                                </>
                              )}
                            </select>
                          </label>
                          {item === "transcription" && (
                            <label className="block">
                              <span className="mb-1 block text-[10px] font-medium text-stone-500">
                                {t("Language")}
                              </span>
                              <input
                                className="field py-1.5 text-xs"
                                value={settings.language ?? ""}
                                placeholder={t("Auto detect")}
                                onChange={(event) =>
                                  onActivitySettingsChange(item, {
                                    ...settings,
                                    language: event.target.value || undefined,
                                  })
                                }
                              />
                            </label>
                          )}
                        </div>
                      )}
                      {item === "video" && (
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-stone-500">
                              {t("Duration (seconds)")}
                            </span>
                            <input
                              type="number"
                              className="field py-1.5 text-xs"
                              min={1}
                              max={120}
                              value={settings.duration ?? ""}
                              placeholder={t("Let the agent choose")}
                              onChange={(event) =>
                                onActivitySettingsChange(item, {
                                  ...settings,
                                  duration: event.target.value
                                    ? Number(event.target.value)
                                    : undefined,
                                })
                              }
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-stone-500">
                              {t("FPS")}
                            </span>
                            <input
                              type="number"
                              className="field py-1.5 text-xs"
                              min={1}
                              max={60}
                              value={settings.fps ?? ""}
                              placeholder={t("Let the agent choose")}
                              onChange={(event) =>
                                onActivitySettingsChange(item, {
                                  ...settings,
                                  fps: event.target.value
                                    ? Number(event.target.value)
                                    : undefined,
                                })
                              }
                            />
                          </label>
                        </div>
                      )}
                      {item === "model3d" && (
                        <div className="space-y-2 rounded-lg border border-lumen-200 p-2 dark:border-lumen-900/50">
                          <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                              <span className="mb-1 block text-[10px] font-medium text-stone-500">
                                {t("Target engine")}
                              </span>
                              <select
                                className="field py-1.5 text-xs"
                                value={settings.engine ?? ""}
                                onChange={(event) =>
                                  onActivitySettingsChange(item, {
                                    ...settings,
                                    engine: (event.target.value ||
                                      undefined) as typeof settings.engine,
                                  })
                                }
                              >
                                <option value="">{t("Generic")}</option>
                                <option value="unity">{t("Unity")}</option>
                                <option value="unreal">{t("Unreal")}</option>
                                <option value="godot">{t("Godot")}</option>
                                <option value="web">{t("Web")}</option>
                              </select>
                            </label>
                            <label className="block">
                              <span className="mb-1 block text-[10px] font-medium text-stone-500">
                                {t("Export format")}
                              </span>
                              <select
                                className="field py-1.5 text-xs"
                                value={settings.outputFormat ?? ""}
                                onChange={(event) =>
                                  onActivitySettingsChange(item, {
                                    ...settings,
                                    outputFormat:
                                      event.target.value || undefined,
                                  })
                                }
                              >
                                <option value="">{t("Engine default")}</option>
                                <option value="glb">{t("GLB")}</option>
                                <option value="fbx">{t("FBX")}</option>
                                <option value="obj">{t("OBJ")}</option>
                                <option value="usdz">{t("USDZ")}</option>
                                <option value="stl">{t("STL")}</option>
                              </select>
                            </label>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                              <span className="mb-1 block text-[10px] font-medium text-stone-500">
                                {t("Target polygons")}
                              </span>
                              <input
                                type="number"
                                className="field py-1.5 text-xs"
                                min={100}
                                max={300000}
                                value={settings.targetPolycount ?? ""}
                                placeholder={t("Provider default")}
                                onChange={(event) =>
                                  onActivitySettingsChange(item, {
                                    ...settings,
                                    targetPolycount: event.target.value
                                      ? Number(event.target.value)
                                      : undefined,
                                  })
                                }
                              />
                            </label>
                            <label className="block">
                              <span className="mb-1 block text-[10px] font-medium text-stone-500">
                                {t("Animation action ID")}
                              </span>
                              <input
                                type="number"
                                className="field py-1.5 text-xs"
                                min={0}
                                value={settings.animationActionId ?? ""}
                                placeholder={t("Optional")}
                                onChange={(event) =>
                                  onActivitySettingsChange(item, {
                                    ...settings,
                                    animationActionId: event.target.value
                                      ? Number(event.target.value)
                                      : undefined,
                                    rig: event.target.value
                                      ? true
                                      : settings.rig,
                                  })
                                }
                              />
                            </label>
                          </div>
                          <div className="flex flex-wrap gap-3 text-[10px]">
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={settings.pbr ?? false}
                                onChange={(event) =>
                                  onActivitySettingsChange(item, {
                                    ...settings,
                                    pbr: event.target.checked || undefined,
                                  })
                                }
                              />
                              {t("PBR")}
                            </label>
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={settings.rig ?? false}
                                onChange={(event) =>
                                  onActivitySettingsChange(item, {
                                    ...settings,
                                    rig: event.target.checked || undefined,
                                  })
                                }
                              />
                              {t("Rig")}
                            </label>
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={settings.lod ?? false}
                                onChange={(event) =>
                                  onActivitySettingsChange(item, {
                                    ...settings,
                                    lod: event.target.checked || undefined,
                                  })
                                }
                              />
                              {t("LOD")}
                            </label>
                          </div>
                        </div>
                      )}
                      {(item === "sprite" || item === "tileset") && (
                        <div className="grid grid-cols-3 gap-2">
                          <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-stone-500">
                              {t("Columns")}
                            </span>
                            <input
                              type="number"
                              className="field py-1.5 text-xs"
                              min={1}
                              max={64}
                              value={settings.columns ?? ""}
                              onChange={(event) =>
                                onActivitySettingsChange(item, {
                                  ...settings,
                                  columns: event.target.value
                                    ? Number(event.target.value)
                                    : undefined,
                                })
                              }
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-stone-500">
                              {t("Rows")}
                            </span>
                            <input
                              type="number"
                              className="field py-1.5 text-xs"
                              min={1}
                              max={64}
                              value={settings.rows ?? ""}
                              onChange={(event) =>
                                onActivitySettingsChange(item, {
                                  ...settings,
                                  rows: event.target.value
                                    ? Number(event.target.value)
                                    : undefined,
                                })
                              }
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-stone-500">
                              {t("Tile size")}
                            </span>
                            <input
                              type="number"
                              className="field py-1.5 text-xs"
                              min={1}
                              max={4096}
                              value={settings.tileSize ?? ""}
                              onChange={(event) =>
                                onActivitySettingsChange(item, {
                                  ...settings,
                                  tileSize: event.target.value
                                    ? Number(event.target.value)
                                    : undefined,
                                })
                              }
                            />
                          </label>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        {(
                          [
                            ["temperature", t("Temperature"), 0, 2, 0.1],
                            ["topP", t("Top P"), 0, 1, 0.05],
                            ["maxTokens", t("Max tokens"), 1, 1000000, 1],
                          ] as const
                        ).map(([key, label, min, max, step]) => (
                          <label key={key} className="block">
                            <span className="mb-1 block text-[10px] font-medium text-stone-500">
                              {label}
                            </span>
                            <input
                              type="number"
                              className="field min-w-0 px-2 py-1.5 text-xs"
                              min={min}
                              max={max}
                              step={step}
                              value={
                                typeof settings[key] === "number"
                                  ? settings[key]
                                  : ""
                              }
                              placeholder={t("Default")}
                              onChange={(event) => {
                                const raw = event.target.value;
                                onActivitySettingsChange(item, {
                                  ...settings,
                                  [key]: raw === "" ? undefined : Number(raw),
                                });
                              }}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {tools.length > 0 && (
            <>
              <p className="px-2 pb-2 text-[11px] font-bold tracking-wider text-stone-400 uppercase">
                {t("Built-in tools")}
              </p>
              {tools
                .filter((tool) => tool.name !== "generate_activity")
                .map((tool) => {
                  const selected = toolIds.includes(tool.id);
                  const hasSettings =
                    Object.keys(
                      (tool.userSchema as { properties?: object }).properties ??
                        {},
                    ).length > 0;
                  return (
                    <div
                      key={tool.id}
                      className="flex items-start gap-2 rounded-lg p-2 hover:bg-stone-50 dark:hover:bg-white/5"
                    >
                      <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
                        <Toggle
                          className="mt-1"
                          checked={selected}
                          onChange={() => onToggle(tool.id)}
                        />
                        <span className="min-w-0">
                          <strong className="block text-sm">
                            {tool.displayName || tool.name}
                          </strong>
                          <span className="block truncate text-xs text-stone-400">
                            {tool.description}
                          </span>
                        </span>
                      </label>
                      {hasSettings && (
                        <button
                          type="button"
                          className="button shrink-0 p-1.5"
                          onClick={() => onOpenSettings(tool.id)}
                          title={`Configure ${tool.displayName || tool.name}`}
                        >
                          <ChevronRight size={16} />
                        </button>
                      )}
                    </div>
                  );
                })}
            </>
          )}
          <div className="mt-2 border-t border-stone-100 pt-3 dark:border-white/10">
            <p className="px-2 pb-2 text-[11px] font-bold tracking-wider text-stone-400 uppercase">
              {t("Activities")}
            </p>
            {(
              Object.keys(activityModels) as Array<
                Exclude<ComposerActivity, "ask">
              >
            ).map((item) => {
              const models = activityModels[item];
              const enabled = enabledActivities.includes(item);
              const configured = activitySettings[item];
              return (
                <div
                  key={item}
                  className="flex items-start gap-2 rounded-lg p-2 hover:bg-stone-50 dark:hover:bg-white/5"
                >
                  <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
                    <Toggle
                      className="mt-1"
                      checked={enabled}
                      disabled={models.length === 0}
                      onChange={() => onToggleActivity(item)}
                    />
                    <span className="min-w-0">
                      <strong className="block text-sm">
                        {activityLabel(item)}
                      </strong>
                      <span className="block truncate text-xs text-stone-400">
                        {models.length === 0
                          ? t("Unavailable")
                          : configured?.modelId
                            ? (models.find(
                                (model) => model.id === configured.modelId,
                              )?.name ?? t("Selected model"))
                            : t("Let the agent choose")}
                      </span>
                    </span>
                  </label>
                  {models.length > 0 && (
                    <button
                      type="button"
                      className={`button shrink-0 p-1.5 ${enabled && configured ? "text-lumen-600" : ""}`}
                      onClick={() => onOpenActivity(item)}
                      title={t("Configure activity")}
                      aria-label={t("Configure activity")}
                    >
                      <Settings2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {mcpServers.length > 0 && (
            <>
              <p className="mt-2 border-t border-stone-100 px-2 pt-3 pb-2 text-[11px] font-bold tracking-wider text-stone-400 uppercase dark:border-white/10">
                {t("MCP servers")}
              </p>
              {mcpServers.map((server) => {
                const selected = mcpServerIds.includes(server.id);
                const needsSetup =
                  server.authType !== "none" &&
                  server.authScope === "user" &&
                  !server.userConfigConfigured;
                const selectedCount = (mcpToolSelections[server.id] ?? [])
                  .length;
                return (
                  <div
                    key={server.id}
                    className="flex items-start gap-2 rounded-lg p-2 hover:bg-stone-50 dark:hover:bg-white/5"
                  >
                    <label
                      className={`flex min-w-0 flex-1 items-start gap-3 ${needsSetup ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                    >
                      <Toggle
                        className="mt-1"
                        disabled={needsSetup}
                        checked={selected}
                        onChange={() => onToggleMcp(server.id)}
                      />
                      <ServerCog
                        size={15}
                        className="mt-0.5 shrink-0 text-lumen-600"
                      />
                      <span className="min-w-0">
                        <strong className="block text-sm">{server.name}</strong>
                        <span className="block truncate text-xs text-stone-400">
                          {selected && mcpTools[server.id]?.status === "ready"
                            ? `${selectedCount} of ${mcpTools[server.id].tools.length} functions selected`
                            : server.description || server.endpointUrl}
                        </span>
                      </span>
                    </label>
                    {needsSetup ? (
                      <button
                        type="button"
                        className="button shrink-0 gap-1 p-1.5 text-xs"
                        onClick={() => onSetupMcp(server)}
                      >
                        {server.authType === "oauth" ? (
                          <LogIn size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}{" "}
                        {server.authType === "oauth" ? t("Connect") : "Set up"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="button shrink-0 p-1.5"
                        onClick={() => onOpenMcp(server.id)}
                        title={`Choose ${server.name} functions`}
                      >
                        <ChevronRight size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </>
  );
}
